/**
 * 스탯 강화 도메인 부트스트래퍼 (S2b) — 골드 영구 강화 탭.
 * 배수(x1/x10/x100/xMax)는 뷰 상태(MultiplierSelector)라 커맨드 호출 시 뷰에서 읽어 전달.
 */
import { StatUpgradeTabView } from "../ui/StatUpgradeTabView.js";
import { buildStatUpgradeData } from "../ui/tabData.js";
import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import type { DomainBootstrapper } from "./BootTypes.js";

export const statUpgradeBoot: DomainBootstrapper = {
  key: "statUpgrade",
  install(boot) {
    boot.tabs.registerLazy("statUpgrade", {
      mount: () => {
        const { tabContent } = boot.layout;
        const data = (multiplier: ReturnType<StatUpgradeTabView["currentMultiplier"]>) =>
          buildStatUpgradeData(
            boot.ctx.statUpgrade(),
            boot.ctx.rebirthUpgrade(),
            boot.ctx.relic(),
            boot.ctx.economy(),
            multiplier,
          );
        const view = new StatUpgradeTabView(
          boot.scene,
          tabContent.x,
          tabContent.y,
          tabContent.width,
          boot.layout.shell.tabPanelBottom,
          {
            onUpgrade: (statId) => boot.progression.upgradeStat(statId, view.currentMultiplier()),
            // 배수 변경 시 각 행의 총비용/구매 가능 즉시 갱신 (currentMultiplier 반영).
            onMultiplierChange: () => view.update(data(view.currentMultiplier())),
          },
        );
        boot.guideQuestHighlight.setStatUpgradeActionRectProvider(() => view.upgradeButtonRect());
        boot.addDisposer(() => boot.guideQuestHighlight.setStatUpgradeActionRectProvider(null));
        // 시그니처(가벼운 입력 요약) — 동일하면 refresh가 update를 스킵 → buildStatUpgradeData(maxAffordable
        // 누진루프×5종) 미실행. GameScene이 250ms 타이머·mutation flush로 매번 refresh해도 데이터 불변 시 비용 0.
        const STAT_IDS = ["attack", "fireRate", "goldGain", "critChance", "critDamage"] as const;
        const signature = (): string => {
          const su = boot.ctx.statUpgrade();
          const ru = boot.ctx.rebirthUpgrade();
          const relic = boot.ctx.relic();
          const gold = boot.ctx.economy().gold;
          // ⚠ 골드 절대값(매 프레임 벽돌 파괴로 증가)을 sig에 넣으면 매번 변해 diff 무효 → buildData 매번(렉).
          // 대신 "각 스탯 강화 가능 여부(affordable)"만 — 골드가 다음 1레벨 비용 임계를 넘을 때만 변(평소엔 불변 → skip).
          return (
            STAT_IDS.map((s) => {
              const lv = su.level(s);
              const affordable = !su.isMaxed(s) && gold.gte(su.unitCostAt(s, lv)) ? 1 : 0;
              return `${lv},${ru.level(s)},${affordable}`;
            }).join("|") +
            `#${view.currentMultiplier()}#${relic.attackMultiplier()},${relic.fireRateMultiplier()},${relic.goldMultiplier()}`
          );
        };
        return viewTabHandle(view, () => view.update(data(view.currentMultiplier())), signature);
      },
    });
  },
};
